import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Pencil, Play, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  PageBody,
  TableActionsMenu,
  type DataTableColumn,
  type DataTableSort,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import {
  ROUTINE_PAGE_SIZE,
  deleteRoutine,
  fetchRoutines,
  runRoutine,
  type InsightRoutine,
  type InsightRoutineListItem,
  type RoutineSortKey,
} from '@/lib/insights.ts';
import { RUN_STATUS_TONE } from '@/components/insights/runStatus.ts';
import { formatRelativeTime } from '@/utils/dateUtils.ts';
import { describeSchedule } from '@/utils/schedule.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { reportError } from '@/lib/reportError.ts';

export const Route = createFileRoute('/_app/insights/')({
  staticData: { orgScope: 'none' },
  component: InsightRoutinesPage,
});

function ownerName(routine: InsightRoutine): string {
  const owner = routine.created_by;
  if (!owner) return '—';

  return [owner.first_name, owner.last_name].filter(Boolean).join(' ') || '—';
}

/** The most recent run: when, how it ended, and the way into it. */
function LastRun({ routine }: { routine: InsightRoutineListItem }) {
  const last = routine.last_run;
  if (!last) return <span className='text-ink-muted'>Never</span>;

  return (
    <Link
      to='/insights/runs/$id'
      params={{ id: last.id }}
      className='flex items-center gap-2'
      /* The row opens the run history; this cell opens the run itself. */
      onClick={(event) => event.stopPropagation()}
    >
      <span
        className='tabular-nums text-ink-secondary underline-offset-2 hover:underline'
        title={new Date(last.ran_at).toLocaleString()}
      >
        {formatRelativeTime(last.ran_at)}
      </span>
      <Badge tone={RUN_STATUS_TONE[last.status]}>{last.status}</Badge>
    </Link>
  );
}

const columns: DataTableColumn<InsightRoutineListItem>[] = [
  { key: 'name', header: 'Name', sortable: true },
  {
    key: 'owner',
    header: 'Owner',
    sortable: true,
    render: ownerName,
    hideBelow: 'mobile',
  },
  {
    key: 'schedule_cron',
    header: 'Schedule',
    hideBelow: 'mobile',
    nowrap: true,
    render: (routine) => (
      <span className={routine.schedule_cron ? undefined : 'text-ink-muted'}>
        {describeSchedule(routine)}
      </span>
    ),
  },
  {
    key: 'last_run',
    header: 'Last run',
    sortable: true,
    nowrap: true,
    render: (routine) => <LastRun routine={routine} />,
  },
  {
    key: 'enabled',
    header: 'Enabled',
    sortable: true,
    nowrap: true,
    render: (routine) => (
      <Badge tone={routine.enabled ? 'success' : 'neutral'}>
        {routine.enabled ? 'on' : 'off'}
      </Badge>
    ),
  },
];

/**
 * Every insight routine: a prompt, when it runs, and where it posts.
 *
 * A row opens its run history rather than an edit form — what a routine did is
 * asked far more often than what it says, and editing is one menu away.
 */
export function InsightRoutinesPage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  /* Newest run first: what the routines did lately is what this page is read for. */
  const [sort, setSort] = useState<DataTableSort>({ key: 'last_run', direction: 'desc' });
  const [deleting, setDeleting] = useState<InsightRoutine | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [runError, setRunError] = useState('');

  const routines = useQuery({
    queryKey: ['insightRoutines', page, sort],
    queryFn: () =>
      fetchRoutines(page, { key: sort.key as RoutineSortKey, direction: sort.direction }),
    /* The rows stay put while the next page lands; only the numbers change. */
    placeholderData: keepPreviousData,
  });

  function invalidateRoutines() {
    return queryClient.invalidateQueries({ queryKey: ['insightRoutines'] });
  }

  const remove = useMutation({
    mutationFn: (routine: InsightRoutine) => deleteRoutine(routine.id),
  });

  /*
   * The dialog stays open on a failure and says so inside itself: closing it
   * would take the only report of what went wrong with it.
   */
  async function confirmDelete() {
    if (!deleting) return;

    setDeleteError('');
    try {
      await remove.mutateAsync(deleting);
      setDeleting(null);
      await invalidateRoutines();
    } catch (failure) {
      reportError(failure, { where: 'insights.deleteRoutine', routineId: deleting.id });
      setDeleteError('Failed to delete the routine');
    }
  }

  /* Running is asking for one iteration now: the run it starts is the answer. */
  const run = useMutation({
    mutationFn: (routine: InsightRoutine) => runRoutine(routine.id),
    onSuccess: (started) => {
      setRunError('');
      void router.navigate({ to: '/insights/runs/$id', params: { id: started.id } });
    },
    onError: (failure) =>
      setRunError(apiErrorMessage(failure, 'Failed to start the routine')),
  });

  const rows = routines.data?.data ?? [];
  const total = routines.data?.meta.totalItems ?? 0;

  return (
    <>
      <PageHeader title='Data insights' />
      <PageBody>
        {runError !== '' && (
          <p
            role='alert'
            className='mb-3.5 rounded-md bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
          >
            {runError}
          </p>
        )}

        <DataTable
          label='Routines'
          columns={columns}
          rows={rows}
          rowKey={(routine) => routine.id}
          loading={routines.isLoading}
          error={routines.isError ? 'Failed to load insight routines' : undefined}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          pagination={{ page, pageSize: ROUTINE_PAGE_SIZE, total }}
          onPageChange={setPage}
          rowHref={(routine) => `/insights/routines/${routine.id}/runs`}
          onNavigate={(href) => void router.navigate({ href })}
          emptyState={
            <EmptyState
              title='No routines yet'
              description='A routine is a prompt Wundo runs on a schedule and posts to Slack.'
            />
          }
          toolbar={
            <Button
              size='sm'
              onClick={() => void router.navigate({ to: '/insights/routines/new' })}
            >
              <Plus className='size-4' aria-hidden='true' />
              New routine
            </Button>
          }
          actions={(routine) => (
            <TableActionsMenu
              label={`Actions for ${routine.name}`}
              items={[
                {
                  label: 'Run now',
                  icon: <Play className='size-4' aria-hidden='true' />,
                  onSelect: () => run.mutate(routine),
                },
                {
                  label: 'Edit',
                  icon: <Pencil className='size-4' aria-hidden='true' />,
                  onSelect: () =>
                    void router.navigate({
                      to: '/insights/routines/$id/edit',
                      params: { id: routine.id },
                    }),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 className='size-4' aria-hidden='true' />,
                  tone: 'danger',
                  onSelect: () => {
                    setDeleteError('');
                    setDeleting(routine);
                  },
                },
              ]}
            />
          )}
        />
      </PageBody>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title='Delete routine?'
        description={`Delete "${deleting?.name ?? ''}"? This removes its runs too.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError}
        onConfirm={confirmDelete}
      />
    </>
  );
}
